import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { FileService, IFileValidator } from '../../../core/service/file.service';
import { FormControlExFactory } from '../../service/factory/form-control-ex-factory.service';

let nextId = 0;

@Component({
    selector: 'app-single-file-upload',
    templateUrl: './single-file-upload.component.html',
    styleUrls: ['./single-file-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SingleFileUploadComponent implements OnInit, OnDestroy {
  constructor(
    protected fb: FormBuilder,
    protected fc: FormControlExFactory,
    private _fileService: FileService
  ) {}
  @Output() readonly filesSelected: EventEmitter<FileList> = new EventEmitter<FileList>();
  @Input() fileValidator: IFileValidator;
  @Input() autoReset: boolean;
  @Input() externalClickEvent: Observable<void>;
  @Input() allowedMimeTypes: string[] = [];

  protected readonly uploadFileId = `uploadfile-${nextId++}`;
  private eventsSubscription: Subscription;
  showAddButton: boolean = true;

  @ViewChild('uploadFile') uploadFileElement: ElementRef;

  fileIsSelected: boolean;
  errorMessage: string = null;

  previewDataUrl: string = null;
  previewMimeType: string = null;
  previewFileName: string = null;

  onFileDropped(files: FileList) {
    this.onFileChange(files);
  }

  ngOnInit(): void {
    if (this.externalClickEvent) {
      this.eventsSubscription = this.externalClickEvent.subscribe(() => {
        if (this.uploadFileElement) {
          this.uploadFileElement.nativeElement.click();
        }
      });
      this.showAddButton = false;
    }
  }

  ngOnDestroy() {
    this.eventsSubscription?.unsubscribe();
  }

  onFileChange(files: FileList) {
    this.errorMessage = null;
    if (files && files.length > 0) {
      for (let index = 0; index < files.length; index++) {
        this.errorMessage = this.errorMessage
          ? this.errorMessage
          : (this.fileValidator ? this.fileValidator : this._fileService).validateDocument(files[index]);
      }
      // Prepare preview for the first file only
      const file = files.item(0);
      this.previewMimeType = file?.type ?? null;
      this.previewFileName = file?.name ?? null;
      this.previewDataUrl = null;
      if (file && this._fileService.isWebImage(file.type)) {
        const reader = new FileReader();
        reader.onload = () => {
          this.previewDataUrl = reader.result as string;
        };
        reader.readAsDataURL(file);
      }
    }
    this.fileIsSelected = this.errorMessage === null;
    this.filesSelected.emit(this.errorMessage ? null : files);
    if (this.fileIsSelected && this.autoReset) {
      this.fileIsSelected = false;
      this.previewDataUrl = null;
      this.previewMimeType = null;
      this.previewFileName = null;
    }
  }

  getClass(mimeType: string): string {
    if (mimeType) {
      if (this._fileService.isPdf(mimeType)) {
        return 't-pdf';
      }
      if (this._fileService.isSpreadsheet(mimeType)) {
        return 't-xls';
      }
      if (this._fileService.isWordProcessor(mimeType)) {
        return 't-doc';
      }
      if (this._fileService.isWebImage(mimeType)) {
        return 't-image';
      }
    }
    return 't-other';
  }

  // Fix sidebar closing after canceling [input=file] dialog window
  onCancel(event: Event): void {
    event.stopPropagation();
  }

  clearFile() {
    this.fileIsSelected = false;
    this.errorMessage = null;
    this.previewDataUrl = null;
    this.previewMimeType = null;
    this.previewFileName = null;
    if (this.uploadFileElement?.nativeElement) {
      this.uploadFileElement.nativeElement.value = '';
    }

    this.filesSelected.emit(null);
  }
}
